<template>
  <div :id="product.product_id" class="mtc-item-container" :class="brand.name">
    <div class="mtc-pkg-container" id="mtc-pkg-container" :class="brand.name">
      <div class="offer-title">{{ product.title }}
        <div class='offer-details' v-on:click="toggleDetails($event)" :class="brand.name">
          View Details<span class="mtc-get-details"><i :class="chevronClass"></i></span>
        </div>
      </div>
      <div class="offer-item numeric-detail">
         <label class="floating--label">{{ estimatedPriceLabel }}</label>
        {{ this.totalRetailCostDisplay() }}
      </div>
      <div class="offer-item savings numeric-detail">
        <label class="floating--label">{{ totalSavingsLabel }}</label>
        {{ this.totalSavings() }}
      </div>
      <div class="offer-item numeric-detail-plan-price">
        <label class="floating--label">Plan Price</label>
        {{ this.planPrice() }}
      </div>
      <div class="button-container">
        <button class="ss-button select-button" :class="brand.name" v-on:click="handleSubmit">
          <span id="spinnerSubmit" class="spinner-border" role="status" aria-hidden="true" v-show="apiProcessing">
          </span>
          Select
        </button>
      </div>
    </div>
    <div class="mtc-pkg-details-scroll" :id="product.product_id" v-show="showDetails">
      <div class="mtc-full-description" :class="brand.name" v-html="product.description"></div>
      <div class="mtc-grid" v-if="showPlanIntervals">
        <div class='mtc-grid-hdr-container'>
          <div class="service-label-hdr">Services</div>
          <div v-for="(serviceTier) in intervalNames(product.interval)" :key="serviceTier" class="service-tier-hdr">{{ intervalLabel(serviceTier) }}</div>
        </div>
        <div v-for="(service) in this.allServices()" :key="service.code" class="mtc-grid-detail-container" v-show="includedService(service, product.interval)">
          <div class="service-label">{{ service }}</div>
          <div v-for="(serviceTier) in intervalNames(product.interval)" :key="serviceTier" class="service-tier-detail">
            <i class="fa fa-circle" v-if="hasService(service, serviceTier)"></i>
          </div>
        </div>
      </div>
      <div class="mtc-disclaimer" :class="brand.name" v-html="product.disclaimer"></div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { useBreadcrumbService } from '@/services/breadcrumb-service'
import { useEshopService } from '@/services/eshop-service'

export default {
  name: 'SelectedProductItem',
  props: {
    brand: Object,
    dealer: Object,
    product: Object,
    offerType: {
      type: String,
      default: 'select'
    },
    maxInterval: Number,
    step: Number,
    category: String
  },
  data: function () {
    const { setSteps } = useBreadcrumbService()
    const { selectEshopPlan } = useEshopService()
    return {
      apiProcessing: false,
      showDetails: false,
      selectEshopPlan,
      setSteps
    }
  },
  computed: {
    ...mapState({
      session: state => state.session,
      productVariants: state => state.productVariants
    }),
    ...mapGetters({
      getUserSelectedVariantsByCategory: ['productVariants/getUserSelectedVariantsByCategory']
    }),
    mtcImage: function () {
      return process.env.VUE_APP_ASSET_HOST + this.offer.image
    },
    chevronClass: function () {
      return this.showDetails ? 'fa fa-chevron-up' : 'fa fa-chevron-down'
    },
    showPlanIntervals () {
      // ShowPlan intervals and check that the plan in question has services
      if (this.brand[this.category] && this.brand[this.category].showPlanIntervals &&
      this.product.interval > 0 && this.intervalNames(this.product.interval).length > 0) {
        return this.brand[this.category].showPlanIntervals
      } else {
        return false
      }
    },
    haveSavings () {
      if ((+this.product.variant_unit_amount.amount - +this.product.variant_unit_amount.retail_amount) < 0) {
        return true
      } else {
        return false
      }
    },
    estimatedPriceLabel () {
      if (this.haveSavings) {
        return `Unplanned ${this.category[0].toUpperCase()}${this.category.slice(1).toLowerCase()} Price Estimate`
      } else {
        return ''
      }
    },
    totalSavingsLabel () {
      if (this.haveSavings) {
        return 'Total Savings'
      } else { return '' }
    }
  },
  methods: {
    toggleDetails: function (event) {
      this.showDetails = !this.showDetails
      if (this.showDetails) {
        this.$store.dispatch('track/gaEcommViewEvent', { vm: this, event: 'view_item', brand: this.brand.name, items: [{ product_id: this.product.product_id, category: this.product.category, title: this.product.title, price: this.product.variant_unit_amount.amount }] })
        this.$store.dispatch('track/facebookEvent', { event: 'View-Item' })
        this.$store.dispatch('session/logInteraction', { interaction_type: 'eshop', action: 'view_item', metadata: this.productPayload() })
        this.setFocus()
      }
    },
    productPayload: function () {
      // Capture the details of the product selected for purchase set to CamelCase for store
      const payload = {}
      payload.code = this.product.product_id
      payload.title = this.product.title
      payload.discountValue = this.product.variant_unit_amount.amount
      payload.retailValue = this.product.variant_unit_amount.retail_amount
      return payload
    },
    setFocus: function () {
      // scrollIntoView smoothly
      const focusDiv = document.getElementById(this.product.product_id)
      const y = focusDiv.offsetTop - (window.innerHeight / 4)
      window.scrollTo({ top: y, left: 0, behavior: 'smooth' }
      )
    },
    planPrice: function () {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.product.variant_unit_amount.amount)
    },
    totalRetailCostDisplay: function () {
      if (this.haveSavings) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.product.variant_unit_amount.retail_amount)
      } else {
        return ''
      }
    },
    totalSavings: function () {
      const totalSavings = +this.product.variant_unit_amount.amount - +this.product.variant_unit_amount.retail_amount
      if (this.haveSavings) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalSavings)
      } else {
        return ''
      }
    },
    hasService: function (src, interval) {
      const matchService = this.$store.getters['prepaidMaintenance/matchService'](interval, src)
      return matchService
    },
    intervalNames: function (count) {
      const serviceDetails = this.$store.getters['prepaidMaintenance/includedIntervals'](count)
      document.documentElement.style.setProperty('--colNum', this.maxInterval)
      return serviceDetails
    },
    intervalLabel: function (serviceTier) {
      return serviceTier.toString().replace('000', '') + 'K'
    },
    allServices: function () {
      const serviceDetails = this.$store.getters['prepaidMaintenance/allService']
      return serviceDetails
    },
    includedService: function (service, interval) {
      const serviceMatch = this.$store.getters['prepaidMaintenance/includedServices'](interval, service)
      return serviceMatch
    },
    matchedCustomerInput () {
    // We join the customer input with the matched variants from the backend
      const variantsArray = []
      const userSelection = this.getUserSelectedVariantsByCategory(this.category)
      for (let i = 0; i < userSelection.length; i++) {
        const variant = {}
        variant.label = userSelection[i].label
        variant.input = userSelection[i].input
        variant.value = this.product.variant_unit_amount.match[i]
        variantsArray.push(variant)
      }
      return variantsArray
    },
    async handleSubmit () {
      this.apiProcessing = true
      await this.selectEshopPlan('product', this.product, this.matchedCustomerInput(), this.brand.name)
      this.setSteps({ currentStep: 3, category: this.category })
      this.$nextTick(() => { this.$router.replace({ name: 'cart', params: { category: this.category } }) })
      this.apiProcessing = false
    }
  }
}
</script>

<style scoped>
.mtc-item-container {
  width: 100%;
  display: inline-grid;
  flex-direction: column;
  margin-bottom: 20px;
  border-bottom: 1px solid #000000;
  padding-bottom: 20px;
}
.mtc-pkg-container {
  display: inline-grid;
  width: 100%;
  font-size: 0.9rem;
  font-weight: bold;
  text-align: left;
  padding: 20px 0px;
  column-gap: 10px;
  row-gap: 10px;
  text-align: center;
  justify-items: center;
}
.offer-details {
  font-size: 0.8rem;
  font-weight: normal;
  cursor: pointer;
}
.mtc-pkg-container.mini { /* Mini content and columns left justified */
  text-align: left;
  justify-items: left;
}
.mtc-pkg-container.bmw { /* BMW content left justified but columns and labels centered */
  justify-items: left;
}
.offer-item {
  margin: auto; /* center line items */
  width: 100%;
  border-radius: .25rem;
  /* text-align: center; */
}
.offer-item > label {
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  margin-bottom: 0; /* Override default `<label>` margin */
  line-height: 1.5;
  color: #878787;
  border: 1px solid transparent;
  border-radius: .25rem;
  transition: all .1s ease-in-out;
}
.savings {
  color: red;
}
.mtc-pkg-details-scroll {
  display: flex;
  flex-direction: column;
  width:100%;
  height: 100%;
  overflow: hidden;
  row-gap: 20px;
  animation: fadeInLoad 2s;
}

@keyframes fadeInLoad {
  from {
    opacity:0;
  }
  to {
    opacity:1;
  }
}
.mtc-grid {
  display: flex;
  flex-direction: column;
  overflow-x: scroll;
  width: 300px;
  height: 100%;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
}
.mtc-grid-hdr-container {
  scroll-snap-align: none;
  display: grid;
  width: 100%;
  grid-template-rows: auto;
  grid-template-columns: 275px repeat(var(--colNum), 50px);
  font-weight: bold;
  overflow-x: visible;
  margin-right: 20px;
  padding: 10px 0px;
}
.mtc-grid-detail-container {
  scroll-snap-align: none;
  display: grid;
  width: 100%;
  grid-template-rows: auto;
  grid-template-columns: 275px repeat(var(--colNum), 50px);
  padding: 10px 0px;
}
.service-tier-hdr {
  text-align: center;
  width: 100%;
  background-color: #c1c1c1;
}
.service-label-hdr {
  text-align: left;
  width: 100%;
  background-color: #c1c1c1;
}
.service-tier-detail {
  grid-column-start: auto;
  grid-row-start: auto;
  height: 100%;
  border-bottom: 1px solid #c1c1c1;
}
.service-label {
  text-align: left;
  width: 100%;
  border-bottom: 1px solid #c1c1c1;
}
.service-label-hdr,
.service-label,
.service-tier-detail {
  scroll-snap-stop: normal;
}
.fa-circle {
  font-size: 8px;
}
.mtc-get-details {
  padding-left: 10px;
}
.mtc-disclaimer {
  font-size: 0.5rem;
}
/* .numeric-detail {
  text-align: center;
} */
.numeric-detail-plan-price {
  /* text-align: center; */
  font-size: 1.2rem;
}
.mtc-full-description,
.mtc-disclaimer {
  text-align: left;
  width: 100%;
}
.select-button {
  display: flex;
  max-height: 40px;
  text-align: center;
  align-items: center;
  align-content: center;
  justify-content: center;
  justify-items: center;
}
.button-container {
    justify-content: center;
    text-align: center;
    align-self: center;
    align-items: center;
  }
/* styles for different screen widths follow */
@media only screen and (min-width: 576px) {
  .mtc-pkg-container {
    padding: 20px 0px;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    grid-template-areas:
      [row1-start] "pkg pkgPrice" [row1-end]
      [row2-start] "retailPrice savings" [row2-end]
      [row4-start] "button button" [row4-end];
  }
  .mtc-grid {
    width: 100%;
  }
  .offer-title {
    font-weight: bold;
    text-align: left;
  }
  .button-container {
    grid-area: button;
    grid-column: 1 /span 2;
    grid-row: 4/4;
  }
}
@media only screen and (min-width: 768px) {
  .mtc-pkg-container {
    grid-template-columns: auto 15% 15% 15%;
    grid-template-areas:
    [row1-start] "pkg retailPrice pkgPrice savings" [row1-end]
    [row2-start] "button button button button" [row2-end];
  }
  .mtc-grid-hdr-container {
    grid-template-columns: 225px repeat(var(--colNum), 50px);
  }
  .mtc-grid-detail-container {
    grid-template-columns: 225px repeat(var(--colNum), 50px);
  }
  .offer-title {
    grid-area: pkg;
    grid-column: 1 / span 1;
    grid-row: 1 / 1;
    font-weight: bold;
    text-align: left;
  }
  .button-container {
    grid-area: button;
    grid-column: 1 /span 4;
    grid-row: 2/2;
  }
  .select-button {
    width: 120px;
    max-height: 40px;
    min-width: 120px;
    max-width: 120px;
  }
  .mtc-service-container {
    display: inline-grid;
  }
  .offer-item {
    border: none;
  }
  .offer-item > label {
    display: none;
  }
}
@media only screen and (min-width: 992px) {
  .mtc-pkg-container {
    grid-template-columns: auto 12% 12% 12% 150px;
    grid-template-areas:
    [row1-start] "pkg retailPrice pkgPrice savings button" [row1-end];
  }
  .mtc-grid-hdr-container {
    grid-template-columns: 275px repeat(var(--colNum), 50px);
  }
  .mtc-grid-detail-container {
    grid-template-columns: 275px repeat(var(--colNum), 50px);
  }
  .button-container {
    grid-area: button;
    grid-column: 5 /span 1;
    grid-row: 1/1;
    overflow: hidden;
  }
}
@media only screen and (min-width: 1200px) {
  .mtc-pkg-container {
    grid-template-columns: auto 15% 15% 15% 150px;
  }
  .mtc-grid-hdr-container {
    grid-template-columns: auto repeat(var(--colNum), 75px);
  }
  .mtc-grid-detail-container {
    grid-template-columns: auto repeat(var(--colNum), 75px);
  }
}
</style>
